import { jwtDecode } from "jwt-decode";
import AppConstants from "../configs/AppConstants";
import EntityConstants from "../configs/EntityConstants";
import Toast from "../common/Toast/Toast";
import { isOtelAlertingEnabled, logError } from "../telemetry";

const API_BASE_URL = AppConstants.HTTPS_HOST;
const REFRESH_URL = EntityConstants.GET_REFRESH_ACCESS_TOKEN;

let isRefreshing = false;
let requestQueue = [];

const stripQueryAndFragment = (value) => value.split(/[?#]/)[0];

const getEndpoint = (url) => {
  if (typeof url !== "string") return "";
  if (API_BASE_URL && url.startsWith(API_BASE_URL)) {
    return stripQueryAndFragment(url.slice(API_BASE_URL.length)) || "/";
  }
  try {
    return new URL(url).pathname;
  } catch {
    return stripQueryAndFragment(url);
  }
};

const extractLocalizedText = (value, fallback) => {
  if (typeof value === "string" && value.trim()) return value;
  if (Array.isArray(value)) {
    const parts = value.filter((item) => typeof item === "string");
    return parts.length ? parts.join(" ") : fallback;
  }
  if (value && typeof value === "object" && ("en" in value || "pt" in value)) {
    const lang =
      (typeof localStorage !== "undefined" &&
        localStorage.getItem("i18nextLng")) ||
      "en";
    const short = String(lang).slice(0, 2);
    return value[short] || value.en || value.pt || fallback;
  }
  return fallback;
};

const extractValidationErrors = (errors) => {
  if (!errors || typeof errors !== "object") return [];
  const collected = [];
  Object.entries(errors).forEach(([key, fieldErrors]) => {
    if (key === "code") return;
    if (Array.isArray(fieldErrors)) {
      fieldErrors.forEach((fieldError) => {
        if (typeof fieldError === "string") collected.push(fieldError);
      });
    } else if (typeof fieldErrors === "string") {
      collected.push(fieldErrors);
    }
  });
  return collected;
};

const readUserFacingApiErrorMessage = async (response) => {
  const fallback = `API request failed with status ${response.status}`;
  try {
    const errorData = await response.clone().json();
    const rawMessage =
      errorData.message || errorData.detail || errorData.error || fallback;
    const baseErrorMessage = extractLocalizedText(
      rawMessage,
      typeof rawMessage === "string" ? rawMessage : fallback,
    );
    const validationErrors = extractValidationErrors(errorData.errors);
    if (validationErrors.length > 0) {
      return `${baseErrorMessage}: ${validationErrors.join(" ")}`;
    }
    return baseErrorMessage;
  } catch {
    return fallback;
  }
};

const forceLogout = (reason = "session_expired") => {
  if (reason === "session_expired") {
    localStorage.setItem("logout_reason", "session_timeout");
  }

  localStorage.clear();
  localStorage.setItem(
    "auth_event",
    JSON.stringify({ type: "LOGOUT", reason: reason, t: Date.now() }),
  );
  while (requestQueue.length > 0) {
    const { reject } = requestQueue.shift();
    reject && reject(new Error("Session expired"));
  }

  if (reason === "session_expired") {
    localStorage.setItem("logout_reason", "session_timeout");
  }

  window.location.replace("/");
};

const isRefreshTokenExpired = () => {
  try {
    const token = localStorage.getItem("refresh_token");
    if (!token) return true;
    const { exp } = jwtDecode(token);
    return Date.now() >= exp * 1000;
  } catch {
    return true;
  }
};

const handleRefreshToken = async () => {
  if (isRefreshTokenExpired()) {
    console.log("Refresh token expired (client) — logging out");
    forceLogout("session_expired");
    return false;
  }
  try {
    const response = await POST(`${REFRESH_URL}`, {
      refresh: localStorage.getItem("refresh_token"),
    });
    const data = await response.json();

    if (response.ok && data.access) {
      localStorage.setItem("auth_token", data.access);
      localStorage.setItem("refresh_token", data.refresh);
      return true;
    }
    console.log("Token refresh failed (server) — logging out");
    forceLogout("session_expired");
    return false;
  } catch (error) {
    console.log("Error in refresh API — logging out", error);
    forceLogout("session_expired");
    return false;
  }
};

const handleRequest = async (url, options) => {
  const method = options?.method || "GET";
  const endpoint = getEndpoint(url);

  let response;
  try {
    response = await fetch(url, options);
  } catch (error) {
    if (error.name !== "AbortError") {
      logError("Failed to connect to the API server", {
        event: "api_network_error",
        endpoint,
        "http.method": method,
        ...(isOtelAlertingEnabled() ? { "http.url": endpoint } : {}),
      });
    }
    throw error;
  }

  if (response.status === 401) {
    let currentResp = {};
    try {
      currentResp = await response.clone().json();
    } catch {
      return response;
    }
    if (
      currentResp.code === "token_not_valid" ||
      currentResp.detail === "Token is invalid or expired"
    ) {
      if (response.url === `${API_BASE_URL}${REFRESH_URL}`) {
        forceLogout("session_expired");
        return null;
      }
      if (!isRefreshing) {
        isRefreshing = true;
        const tokenRefreshed = await handleRefreshToken();
        if (tokenRefreshed) {
          requestQueue.forEach(({ url, options, resolve, reject }) => {
            options.headers["authorization"] = `Bearer ${localStorage.getItem(
              "auth_token",
            )}`;
            handleRequest(url, options).then(resolve).catch(reject);
          });
          requestQueue = [];
          isRefreshing = false;

          // Retry the original request with the new token
          options.headers["authorization"] = `Bearer ${localStorage.getItem(
            "auth_token",
          )}`;

          return handleRequest(url, options);
        } else {
          return null;
        }
      } else {
        // Queue the request
        return new Promise((resolve, reject) => {
          requestQueue.push({ url, options, resolve, reject });
        });
      }
    }
  }

  // Alert only on application/server failures. 4xx is usually user input or auth.
  if (!response.ok && response.status >= 500) {
    const errorMessage = isOtelAlertingEnabled()
      ? await readUserFacingApiErrorMessage(response)
      : `API request failed with status ${response.status}`;
    logError(errorMessage, {
      event: "api_error",
      "http.status_code": response.status,
      "http.method": method,
      endpoint,
    });
  }

  if (response.status === 422) {
    const data = await response.json();
    console.log(data);
  }

  return response;
};

export const GET = async (url, options = {}) => {
  const { signal, params, body, skipAuth, ...otherOptions } = options;

  let fullUrl;
  if (params === null || params === undefined) {
    fullUrl = `${API_BASE_URL}${url}`;
  } else {
    fullUrl = `${API_BASE_URL}${url}/?${params}`;
  }

  const headers = {
    "X-CSRFToken": localStorage.getItem("csrf_token"),
    "Accept-Language": localStorage.getItem("i18nextLng"),
  };

  if (!skipAuth && localStorage.getItem("auth_token")) {
    headers.authorization = `Bearer ${localStorage.getItem("auth_token")}`;
  }

  const fetchOptions = {
    ...otherOptions,
    method: "GET",
    headers: headers,
    signal: signal,
  };

  if (body) {
    Toast({
      type: "warning",
      title: "Warning",
      message:
        "GET requests should not have a request body. Use POST instead or move data to query parameters.",
    });
  }

  return handleRequest(fullUrl, fetchOptions);
};

export const POST = async (url, body, extra = {}) => {
  const fullUrl = `${API_BASE_URL}${url}`;
  const options = {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-CSRFToken": `${localStorage.getItem("csrf_token")}`,
      authorization: `Bearer ${localStorage.getItem("auth_token")}`,
      "Accept-Language": localStorage.getItem("i18nextLng"),
    },
    body: JSON.stringify(body),
    ...(extra.signal ? { signal: extra.signal } : {}),
  };

  return handleRequest(fullUrl, options);
};

export const PUT = async (url, body) => {
  const fullUrl = `${API_BASE_URL}${url}`;
  const options = {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      "X-CSRFToken": localStorage.getItem("csrf_token"),
      authorization: `Bearer ${localStorage.getItem("auth_token")}`,
      "Accept-Language": localStorage.getItem("i18nextLng"),
    },
    body: JSON.stringify(body),
  };

  return handleRequest(fullUrl, options);
};

export const DEL = async (url, body) => {
  const fullUrl = `${API_BASE_URL}${url}`;
  const options = {
    method: "DELETE",
    headers: {
      "Content-Type": "application/json",
      "X-CSRFToken": localStorage.getItem("csrf_token"),
      authorization: `Bearer ${localStorage.getItem("auth_token")}`,
      "Accept-Language": localStorage.getItem("i18nextLng"),
    },
    body: JSON.stringify(body),
  };

  return handleRequest(fullUrl, options);
};

export const POSTFORMDATA = async (url, formData) => {
  const fullUrl = `${API_BASE_URL}${url}`;
  const options = {
    method: "POST",
    headers: {
      "X-CSRFToken": localStorage.getItem("csrf_token"),
      authorization: `Bearer ${localStorage.getItem("auth_token")}`,
      "Accept-Language": localStorage.getItem("i18nextLng"),
    },
    body: formData,
  };

  return handleRequest(fullUrl, options);
};

export const LOGOUT = async (url, body) => {
  const fullUrl = `${API_BASE_URL}${url}`;
  const options = {
    method: "POST",
    headers: {
      "X-CSRFToken": localStorage.getItem("csrf_token"),
      authorization: `Bearer ${localStorage.getItem("refresh_token")}`,
      "Accept-Language": localStorage.getItem("i18nextLng"),
    },
    body: JSON.stringify(body),
  };

  return handleRequest(fullUrl, options);
};

export const GENLINK = (url) => {
  return `${API_BASE_URL}${url}?token=${localStorage.getItem("auth_token")}`;
};
